import { computed, toValue } from "vue";
import { useAbility } from "@casl/vue";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import MsoService, { type TMso, type TMsoWrite } from "@/api/models/practice/mso";
import {
  useGenericPaginatedQuery,
  useGenericQuery,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import type {
  TComposableParams,
  TItemQueryComposableParams,
} from "@/composables/shared/filters/useGeneralFilter";
import {
  splitComposableParams,
  resolveSelectEnabled,
} from "@/composables/shared/filters/useGeneralFilter";
import type { TSelectFieldDynamicProps } from "@/types/selectField";
import { createQueryKeys } from "@/composables/shared/queries";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

const MsoQueryKeys = createQueryKeys(MsoService.queryKey, (key) => ({
  detail: key<string>("detail"),
  paginated: key<DefaultPaginatedFilters>("paginated"),
}));

const useCanReadMsos = () => {
  const { can } = useAbility();

  return computed(
    () =>
      can($permActions.READ, $permSubjects.PRACTICE__MSO) ||
      can($permActions.MSO_ME_VIEW, $permSubjects.PRACTICE__MSO)
  );
};

export const useMso = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  const canRead = useCanReadMsos();
  const isEnabled = computed(() => (toValue(params.enabled) ?? true) && canRead.value);

  return useGenericQuery<TMso, string>({
    queryKey: MsoQueryKeys.detail(),
    params: { ...params, enabled: isEnabled },
    fetcher: (id) => MsoService.get({ id }),
    ...options,
  });
};

export const useMyMsos = () => {
  const query = useMsosPaginated();

  const msos = computed<TMso[]>(
    () => query.data.value?.pages.flatMap((page) => page.results) ?? []
  );

  return { msos, isLoading: query.isLoading, query };
};

export const useMsosPaginated = (params: TOwnedFilterParams<DefaultPaginatedFilters> = {}) => {
  const canRead = useCanReadMsos();
  const isEnabled = computed(() => (toValue(params.enabled) ?? true) && canRead.value);

  return useGenericPaginatedQuery({
    params: { ...params, enabled: isEnabled },
    queryKey: MsoQueryKeys.paginated(),
    fetcher: (fetchParams) =>
      MsoService.getPaginatedFiltered({}, fetchParams.filters, fetchParams.pageParam),
  });
};

export const useCreateMso = () => {
  return useGenericMutation<TMso, TMsoWrite>({
    mutationFn: (data) => MsoService.create({}, data),
    invalidates: [MsoQueryKeys.paginated()],
  });
};

export const useUpdateMso = () => {
  return useGenericMutation<TMso, TMsoWrite>({
    mutationFn: (data) => MsoService.update({ id: data?.id! }, data),
    invalidates: (data) => [MsoQueryKeys.detail(data.id), MsoQueryKeys.paginated()],
  });
};

export const useMsosForSelect = (
  extraParams?: TComposableParams & Partial<DefaultPaginatedFilters>
): TSelectFieldDynamicProps<TMso, DefaultPaginatedFilters> => {
  const { filters } = splitComposableParams<DefaultPaginatedFilters>(extraParams);
  const query = useMsosPaginated({
    initial: filters,
    enabled: () => resolveSelectEnabled(extraParams),
  });

  return {
    paginatedQuery: query,
    queryFilters: query.filters,
    filterField: "query",
    queryEnabled: query.queryEnabled,
  };
};
